import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ExcelPreviewModal } from '../components';
import '../styles/OwnershipDataSheet.scss';

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const ViewIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const ELEMENT = 'ownership';

const OwnershipDataSheet = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [savedFilename, setSavedFilename] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [result, setResult] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/core/scorecard/download_template/?element=ownership', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Ownership Data Sheet Template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setUploadError('Template not found on server.');
    }
  };

  useEffect(() => {
    api.get('/core/scorecard/get_scorecard/?element=ownership')
      .then((res) => {
        if (res.data?.filename) setSavedFilename(res.data.filename);
        if (res.data?.total_points != null) setResult(res.data);
      })
      .catch(() => {});
  }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('element', ELEMENT);

      const res = await api.post('/core/scorecard/calculate/', formData);
      setSavedFilename(file.name);
      setResult(res.data);
      fileInputRef.current.value = '';
    } catch (err) {
      setUploadError(err.response?.data?.error || err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const maxPoints = result?.indicators?.reduce((s, ind) => s + (ind.points || 0), 0) || null;

  return (
    <div className="od-container">
      <div className="od-header">
        <button className="od-back" onClick={() => navigate('/ownership')}>← Back</button>
        <div>
          <h1>Data Sheet</h1>
          <p>Ownership — Template &amp; Completed Sheet</p>
        </div>
      </div>

      <div className="od-section-label">Download the template or upload your completed sheet</div>

      <div className="od-grid">
        <button
          className="od-card"
          onClick={handleDownloadTemplate}
        >
          <div className="od-card-icon"><DownloadIcon /></div>
          <span className="od-card-label">Download Template</span>
        </button>

        <button
          className={`od-card ${savedFilename ? 'od-card--uploaded' : ''}`}
          onClick={() => fileInputRef.current.click()}
          disabled={uploading}
        >
          <div className="od-card-icon"><UploadIcon /></div>
          <span className="od-card-label">Completed Data Sheet</span>
          {savedFilename && (
            <span className="od-card-status">✓ {savedFilename}</span>
          )}
        </button>

        {savedFilename && (
          <button className="od-card" onClick={() => setViewModalOpen(true)}>
            <div className="od-card-icon"><ViewIcon /></div>
            <span className="od-card-label">View Uploaded Sheet</span>
          </button>
        )}
      </div>

      {uploadError && (
        <div className="od-error">{uploadError}</div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        accept=".xlsx"
        style={{ display: 'none' }}
        onChange={(e) => handleUpload(e.target.files[0])}
      />

      <ExcelPreviewModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        fetchUrl="/core/scorecard/download_data_sheet/?element=ownership"
        title={savedFilename}
      />

      {result && (
        <div className="od-result">
          <div className="od-result-header">
            <strong>Calculation complete</strong>
          </div>
          <div className="od-result-score">
            <span className="od-result-points">{result.total_points}</span>
            <span className="od-result-max">/ {maxPoints ?? '—'} points</span>
          </div>
          <button
            className="od-result-btn"
            onClick={() => navigate('/ownership/scorecard')}
          >
            View Full Scorecard →
          </button>
        </div>
      )}
    </div>
  );
};

export default OwnershipDataSheet;
